import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useSnackbar } from "notistack";
import theme from "../../theme";
import CommonButton from "../button";
import CommonFormDropDown from "../formdropdown";
import CommonSwitch from "../switch";
import { isSuper } from "../../lib/permissions";
import { DataTable, PaginationBar, BudgetBar } from "./tableKit";
import { ActiveChip, CategoryChip } from "./StatusChip";
import BonusFormDrawer from "./BonusFormDrawer";
import ConfirmDialog from "./ConfirmDialog";
import {
  formatCurrency,
  formatDate,
  CATEGORY_OPTIONS,
  ACTIVE_FILTER_OPTIONS,
  PAGE_SIZE,
  toMessages,
} from "./utils";
import {
  getBonusListAction,
  getBonusListErrorAction,
  createBonusAction,
  createBonusSuccessAction,
  createBonusErrorAction,
  updateBonusAction,
  updateBonusSuccessAction,
  updateBonusErrorAction,
  deleteBonusAction,
  deleteBonusSuccessAction,
  deleteBonusErrorAction,
} from "../../containers/dashboard/action";

const useStyles = makeStyles(() => ({
  toolbar: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  filters: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterField: { width: "190px" },
  filterLabel: {
    fontSize: "13px",
    color: theme.colors.Violet,
    marginBottom: "5px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
  },
  actionsCell: { display: "flex", gap: "4px", justifyContent: "flex-end" },
  createBtn: { width: "170px" },
}));

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All categories" },
  ...CATEGORY_OPTIONS,
];

function BonusesTable() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // Filters / pagination
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState("");
  const [page, setPage] = useState(1);

  // Drawer + dialog
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingBonus, setEditingBonus] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  // Redux state
  const bonusList = useSelector((s) => s.dashboardReducer?.bonusList);
  const loading = useSelector((s) => !!s.dashboardReducer?.bonusListLoading);
  const listError = useSelector((s) => s.dashboardReducer?.bonusListError);
  const mutationLoading = useSelector(
    (s) => !!s.dashboardReducer?.bonusMutationLoading
  );
  const fieldErrors = useSelector((s) => s.dashboardReducer?.bonusFormFieldErrors);
  const createSuccess = useSelector((s) => s.dashboardReducer?.createBonusSuccess);
  const createError = useSelector((s) => s.dashboardReducer?.createBonusError);
  const updateSuccess = useSelector((s) => s.dashboardReducer?.updateBonusSuccess);
  const updateError = useSelector((s) => s.dashboardReducer?.updateBonusError);
  const deleteSuccess = useSelector((s) => s.dashboardReducer?.deleteBonusSuccess);
  const deleteError = useSelector((s) => s.dashboardReducer?.deleteBonusError);

  // Bonuses are view-only for support; create/edit/delete are SUPER-ONLY.
  const me = useSelector((s) => s.loginReducer?.me);
  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    storedUser = null;
  }
  const canManage = isSuper(me || storedUser);

  const items = bonusList?.data || [];
  const count = bonusList?.count || 0;

  const buildFilters = useCallback(
    (overrides = {}) => ({
      category: category || undefined,
      is_active: isActive || undefined,
      page,
      page_size: PAGE_SIZE,
      ...overrides,
    }),
    [category, isActive, page]
  );

  const refetch = useCallback(
    (overrides) => dispatch(getBonusListAction(buildFilters(overrides))),
    [dispatch, buildFilters]
  );

  // Fetch whenever filters / page change.
  useEffect(() => {
    dispatch(getBonusListAction(buildFilters()));
  }, [dispatch, buildFilters]);

  /* ----------------------- notistack + state resets ---------------------- */
  // List errors
  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load bonuses.").forEach((msg) =>
      enqueueSnackbar(msg, {
        variant: "error",
        onClose: () => dispatch(getBonusListErrorAction(null)),
      })
    );
    dispatch(getBonusListErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  // Create
  useEffect(() => {
    if (!createSuccess) return;
    enqueueSnackbar("Bonus created successfully.", { variant: "success" });
    dispatch(createBonusSuccessAction(null));
    setDrawerOpen(false);
    setEditingBonus(null);
    setPage(1);
    refetch({ page: 1 });
  }, [createSuccess, enqueueSnackbar, dispatch, refetch]);

  useEffect(() => {
    if (!createError) return;
    toMessages(createError, "Unable to create bonus.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    // Keep inline field errors visible; only clear the toast trigger.
    dispatch(createBonusErrorAction(null, fieldErrors));
  }, [createError, enqueueSnackbar, dispatch, fieldErrors]);

  // Update
  useEffect(() => {
    if (!updateSuccess) return;
    enqueueSnackbar("Bonus updated successfully.", { variant: "success" });
    dispatch(updateBonusSuccessAction(null));
    setTogglingId(null);
    setDrawerOpen(false);
    setEditingBonus(null);
    refetch();
  }, [updateSuccess, enqueueSnackbar, dispatch, refetch]);

  useEffect(() => {
    if (!updateError) return;
    toMessages(updateError, "Unable to update bonus.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    setTogglingId(null);
    dispatch(updateBonusErrorAction(null, fieldErrors));
  }, [updateError, enqueueSnackbar, dispatch, fieldErrors]);

  // Delete
  useEffect(() => {
    if (!deleteSuccess) return;
    enqueueSnackbar("Bonus deleted.", { variant: "success" });
    dispatch(deleteBonusSuccessAction(null));
    setDeleteTarget(null);
    // If the last row on a page was removed, step back a page.
    const remaining = items.length - 1;
    const nextPage = remaining <= 0 && page > 1 ? page - 1 : page;
    if (nextPage !== page) setPage(nextPage);
    else refetch();
  }, [deleteSuccess, enqueueSnackbar, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deleteError) return;
    // e.g. "bonus has claims" — show message and keep the row in place.
    toMessages(deleteError, "Unable to delete bonus.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(deleteBonusErrorAction(null));
    setDeleteTarget(null);
  }, [deleteError, enqueueSnackbar, dispatch]);

  /* ------------------------------ handlers ------------------------------- */
  const openCreate = () => {
    setDrawerMode("create");
    setEditingBonus(null);
    setDrawerOpen(true);
  };

  const openEdit = (bonus) => {
    setDrawerMode("edit");
    setEditingBonus(bonus);
    setDrawerOpen(true);
  };

  const handleSubmit = (body) => {
    if (drawerMode === "edit" && editingBonus) {
      dispatch(updateBonusAction(body, editingBonus.id));
    } else {
      dispatch(createBonusAction(body));
    }
  };

  const handleToggleActive = (bonus, nextValue) => {
    setTogglingId(bonus.id);
    dispatch(updateBonusAction({ is_active: nextValue }, bonus.id));
  };

  const columns = useMemo(
    () => [
      { key: "name", header: "Name", skeletonWidth: "80%", render: (r) => r.name || "—" },
      {
        key: "category",
        header: "Category",
        render: (r) => <CategoryChip category={r.category} />,
      },
      {
        key: "amount",
        header: "Amount",
        render: (r) => formatCurrency(r.amount),
      },
      {
        key: "budget",
        header: "Budget",
        render: (r) => (
          <BudgetBar totalRedeemed={r.total_redeemed} budgetLimit={r.budget_limit} />
        ),
      },
      {
        key: "claims_count",
        header: "Claims",
        align: "center",
        render: (r) => r.claims_count ?? 0,
      },
      {
        key: "is_active",
        header: "Active",
        stopPropagation: true,
        render: (r) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {canManage ? (
              <CommonSwitch
                checked={!!r.is_active}
                disabled={mutationLoading && togglingId === r.id}
                onChange={(e) => handleToggleActive(r, e.target.checked)}
                inputProps={{ "aria-label": `Toggle ${r.name} active` }}
              />
            ) : null}
            <ActiveChip active={!!r.is_active} />
          </div>
        ),
      },
      {
        key: "created_at",
        header: "Created",
        render: (r) => formatDate(r.created_at),
      },
      // Edit / delete are super-only.
      ...(canManage
        ? [
          {
            key: "actions",
            header: "",
            align: "right",
            stopPropagation: true,
            render: (r) => (
              <div className={classes.actionsCell}>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${r.name}`}
                    onClick={() => openEdit(r)}
                  >
                    <EditOutlinedIcon fontSize="small" style={{ color: theme.colors.DodgerBlue }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    aria-label={`Delete ${r.name}`}
                    onClick={() => setDeleteTarget(r)}
                  >
                    <DeleteOutlineIcon fontSize="small" style={{ color: theme.colors.RejectRed }} />
                  </IconButton>
                </Tooltip>
              </div>
            ),
          },
        ]
        : []),
    ],
    [classes.actionsCell, mutationLoading, togglingId, canManage] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div>
      <div className={classes.toolbar}>
        <div className={classes.filters}>
          <div className={classes.filterField}>
            <div className={classes.filterLabel}>Category</div>
            <CommonFormDropDown
              placeholder="All categories"
              options={CATEGORY_FILTER_OPTIONS}
              value={
                CATEGORY_FILTER_OPTIONS.find((o) => o.value === category) ||
                CATEGORY_FILTER_OPTIONS[0]
              }
              onChange={(opt) => {
                setPage(1);
                setCategory(opt?.value || "");
              }}
            />
          </div>
          <div className={classes.filterField}>
            <div className={classes.filterLabel}>Status</div>
            <CommonFormDropDown
              placeholder="All"
              options={ACTIVE_FILTER_OPTIONS}
              value={
                ACTIVE_FILTER_OPTIONS.find((o) => o.value === isActive) ||
                ACTIVE_FILTER_OPTIONS[0]
              }
              onChange={(opt) => {
                setPage(1);
                setIsActive(opt?.value || "");
              }}
            />
          </div>
        </div>
        {canManage ? (
          <div className={classes.createBtn}>
            <CommonButton
              type="button"
              text="Create bonus"
              stylevariant="primary"
              fontSize="15px"
              onClick={openCreate}
            />
          </div>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        skeletonRows={PAGE_SIZE}
        emptyTitle="No bonuses yet"
        emptyHint="Create your first bonus to get started."
      />

      <PaginationBar
        count={count}
        page={page}
        next={bonusList?.next}
        previous={bonusList?.previous}
        pageSize={PAGE_SIZE}
        onChange={(value) => setPage(value)}
      />

      <BonusFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        bonus={editingBonus}
        loading={mutationLoading}
        fieldErrors={fieldErrors}
        onSubmit={handleSubmit}
        onClose={() => {
          if (mutationLoading) return;
          setDrawerOpen(false);
          setEditingBonus(null);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete bonus?"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” will be permanently removed. Bonuses that already have claims cannot be deleted.`
            : ""
        }
        confirmText="Delete"
        loading={mutationLoading}
        onConfirm={() => deleteTarget && dispatch(deleteBonusAction(deleteTarget.id))}
        onClose={() => {
          if (mutationLoading) return;
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}

export default BonusesTable;
